import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

import { secondJune2026ArticleMeta } from './second-june-2026-article';

import HeaderImage from 'assets/resources/flow-maps-erosion-modeling/header-section.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title:
    'Beyond the Surface: How Flow Maps and Erosion Modeling Add Intelligence to Simulated Terrain',
  publishDate: 'June 3, 2026',
  minutesOfRead: 6,
};

export const thirdJune2026ArticleMeta: PageMeta = {
  url: '/articles/flow-maps-erosion-modeling-terrain',
  title: commonData.title,
  description:
    'Flow maps and erosion modeling turn static terrain into physics-based, dynamic environments, giving autonomous systems realistic surface conditions to train and test against.',
};

export const thirdJune2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'Flow Maps and Erosion Modeling in Simulated Terrain',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          <em>Part 2 in the scenery reconstruction series</em>
        </p>
        <p>
          Reconstructing a real place is the first step. The next is making that terrain behave like
          the real world, where water moves, surfaces change, and conditions rarely stay still. This
          is a continuation of{' '}
          <a href={secondJune2026ArticleMeta.url}>
            Building Real Worlds in Simulation: Scenery Reconstruction for Autonomous Systems
          </a>
          .
        </p>
      </>
    ),
    ...commonData,
  },

  {
    type: 'common',
    title: 'The Problem with Terrain That Does Not Move',
    text: (
      <>
        <p>
          Real terrain is not static. Rain carves channels. Wind erodes exposed surfaces. Water pools
          in low spots and drains along paths determined by slope and soil composition. Any
          autonomous system operating outdoors encounters these dynamics constantly, and its
          perception and navigation models need to handle them.
        </p>
        <p>
          But most simulation environments treat terrain as a fixed 3D mesh. The surface never
          changes, and erosion never happens. Models trained in these environments learn to navigate
          a world that does not exist. When they encounter the real thing, they are underprepared for
          conditions that seem obvious in hindsight: a flooded access road, a degraded runway surface,
          a slope that has shifted after heavy rain.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Why This Gap Matters for Operational Systems',
    text: (
      <>
        <p>
          The cost of static terrain shows up where it hurts most: defense drone fleets misreading
          wet versus dry ground, automated taxiing systems trained on pristine tarmac that fail on a
          rain-streaked runway, port autonomy stacks that have never seen the surface conditions they
          will operate in for ten months of the year. Without terrain that behaves like the real
          world, the data feeding perception models is biased toward conditions that rarely match
          deployment, and the failure cases surface in the field instead of in simulation.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'What Flow Maps and Erosion Models Bring to the Table',
    text: (
      <>
        <p>
          A flow map is a computed data layer that shows the natural direction of water movement
          across terrain. It is derived from elevation data and slope analysis, and it predicts where
          water will accumulate, how it will drain, and which surfaces will be affected by runoff.
          Erosion modeling extends this by simulating how water movement reshapes terrain features
          over time.
        </p>
        <p>
          Together, these two layers turn a static terrain surface into something that behaves more
          like the real world. They are not visual filters. They are physics-based datasets that
          inform how sensors, surfaces, and scenarios interact within the simulation.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'What This Changes for Sensor Data and Scenario Quality',
    text: (
      <>
        <p>
          Flow maps affect how teams reason about synthetic sensor outputs. Knowing where water
          accumulates, where surfaces stay wet, and where runoff reshapes the ground gives scenario
          authors a physical basis for varying lighting, reflectivity, and surface conditions in their
          test cases, rather than relying on visual estimation alone.
        </p>
        <p>
          Erosion data informs how scenarios are constructed. Where a flow model identifies areas
          prone to runoff and surface change, teams can deliberately design scenarios around those
          features instead of treating the terrain as uniform, which broadens the conditions an
          autonomous system is tested against without rebuilding the environment from scratch.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'How Flow Maps and Erosion Modeling Connect to the Rest of the Platform',
    text: (
      <>
        <p>
          These dynamic terrain layers are not a separate product but an extension of the scenery
          reconstruction pipeline, and they sit inside the same platform as the Automated Training
          Pipeline (ATP), Intelligent System Testing (IST), and Simulation-in-the-Loop (SiL) execution
          against the customer&apos;s own AI stack. Keeping environment authoring, sensor simulation,
          and test execution in one platform removes the hand-offs and re-tooling that fragment most
          simulation workflows.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Where Terrain Dynamics Matter Most in Practice',
    text: (
      <>
        <p>
          A typical scenery reconstruction workflow has three steps. First, build the digital version
          of the real location from satellite imagery, elevation data, and vector features. Second,
          modify it for the scenario you need to test: clear a section of forest, drop in a temporary
          structure, add or remove vehicles and obstacles on the surface. Third, run the simulation
          with the modified scene and evaluate how the autonomous system performs against it.
        </p>
        <p>
          The point of the loop is iteration. Once the reconstructed environment exists, variations
          are cheap. You can run the same mission across a hundred versions of the same place with
          different vegetation, different obstacle layouts, different weather, different times of day,
          and compare results without going back to the field. That is the practical value: not
          predicting how nature reshapes itself but giving engineering teams a controlled environment
          to test what the system does when conditions change.
        </p>
        <p>
          Defense applications make this concrete. A drone perception model trained only on summer
          footage of a region will behave differently in winter, after a fresh snowfall, or under low
          cloud. Reconstructing the area of interest and running the model across seasonal variants
          surfaces those gaps before deployment. Aviation programs use the same approach for taxiing
          systems, swapping in wet, dry, and worn tarmac textures across the same airport. Port
          operators rebuild their terminal with realistic crane positions, container stacks, and
          vehicle traffic, then test how their autonomous fleet handles the layout it will meet on day
          one.
        </p>
        <p>
          In each case, the value is the same. The simulation reflects a specific real place, with the
          specific features that matter for the system being tested.
        </p>
      </>
    ),
  },
];

export const thirdJune2026ArticleCard: TCardProps = {
  description:
    'Flow maps and erosion modeling turn static terrain into physics-based, dynamic environments, giving autonomous systems realistic surface conditions to train and test against.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: thirdJune2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.AISimulation,
    EHashtag.DigitalTwin,
    EHashtag.SyntheticData,
    EHashtag.AutonomousVehicles,
    EHashtag.DefenseTechnology,
  ],
};
